






















































































































































import Vue from 'vue';
import {
  Component, Inject, Watch,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import { Route } from 'vue-router';
import { DataTableHeader } from 'vuetify';
import { KCardHeader, KCardHeaderBtn } from '@kasasa/fbase-components';
import { Alert, NoticeClass, Dialog } from '@kasasa/fbase-components/lib';
import {
  EngagementReward, EngagementRewardVersion, EngagementRewardResource, EngagementRewardTypeNames, EngagementRewardDeleteResponse,
} from '@/api/engagementrewards';
import EngagementRewardEditor from '@/components/EngagementRewardEditor.vue';
import EngagementRewardVersionSelector from '@/components/EngagementRewardVersionSelector.vue';
import { DateTool } from '@/util';
import {
  ProductMapping, ProductMappingResource,
} from '@/api/productmapping';

interface RewardTableItem {
  rewardType: string,
  rewardName: string,
  productNames: string,
  active: string,
  id: number,
  designStartDate: string,
}

@Component({
  computed: mapState(['fiId']),
  components: {
    KCardHeader, KCardHeaderBtn, EngagementRewardEditor, EngagementRewardVersionSelector,
  },
})
export default class EngagementRewards extends Vue {
  fiId!: string;

  loading: boolean = false;

  saving: boolean = false;

  editPanelFormValid: boolean = false;

  selectedReward: EngagementReward | null = null;

  workingRewardVersion: EngagementRewardVersion | null = null;

  allRewards: Map<string, EngagementReward> = new Map<string, EngagementReward>();

  originalStartDate: string | null = null;

  title: string = 'Engagement Rewards';

  tableItems: RewardTableItem[] = [];

  isEditable: boolean = true;

  isFormValid: boolean = false;

  loadedFiId: string | null = null;

  productMap: Map<number, ProductMapping> = new Map();

  editingExisting = false;

  @Inject('EngagementRewardResource')
  engagementRewardResource!: EngagementRewardResource;

  @Inject('ProductMappingResource')
  productMappingResource!: ProductMappingResource;

  rewardHeaders: DataTableHeader[] = [
    { text: '', value: 'edit', sortable: false },
    { text: 'Engagement Reward Type', value: 'rewardType', sortable: true },
    { text: 'Engagement Reward Name', value: 'rewardName', sortable: true },
    { text: 'Product Name(s)', value: 'productNames', sortable: true },
    { text: 'Active', value: 'active', sortable: true },
  ];

  rewardsHeaderItems: any = [
    {
      text: 'Rewards Configuration',
      disabled: false,
      link: false,
    },
    {
      text: 'Engagement Rewards',
      disabled: true,
      link: false,
    },
  ];

  rewardHeaderItems: any = [
    {
      text: 'Rewards Configuration',
      disabled: true,
      link: false,
    },
    {
      text: 'Engagement Rewards',
      disabled: false,
      link: true,
    },
    {
      text: 'Reward',
      disabled: true,
      link: false,
    },
  ];

  onNewReward() {
    this.$router.push({ name: 'newEngagementReward', params: { clientId: this.$route.params.clientId } });
  }

  onEditReward(id: number, designStartDate: string) {
    this.$router.push({ name: 'engagementReward', params: { clientId: this.$route.params.clientId, rewardId: id.toString(), designStartDate } });
  }

  viewAllRewards() {
    this.workingRewardVersion = null;
    this.selectedReward = null;
    this.$router.push({ name: 'engagementRewards', params: { clientId: this.$route.params.clientId } });
  }

  @Watch('$route', { immediate: true, deep: true })
  onRouteChange() {
    this.selectedReward = null;
    this.workingRewardVersion = null;
    this.isEditable = false;
    this.title = 'Engagement Rewards';
    const editMode:boolean = this.$route.name === 'engagementReward' || this.$route.name === 'newEngagementReward';
    if (this.fiId !== this.loadedFiId) {
      this.rewardHeaderItems[1].href = `#/${this.$route.params.clientId}/engagementRewards`;
      this.loadRewards(editMode);
      this.selectedReward = null;
      this.workingRewardVersion = null;
    } else if (this.allRewards.size === 0 && !this.loading) {
      this.loadRewards(editMode);
    } else if (editMode) {
      this.editReward();
    }
  }

  onReset() {
    this.loading = false;
    this.allRewards = new Map<string, EngagementReward>();
    this.onRouteChange();
  }

  onBackClicked() {
    this.viewAllRewards();
  }

  @Watch('workingRewardVersion')
  onNewWorkingVersion() {
    if (this.workingRewardVersion) {
      this.title = this.workingRewardVersion!.name;
    }
  }

  loadRewards(editMode: boolean) {
    this.loading = true;
    this.productMap.clear();
    this.tableItems = [];
    this.allRewards.clear();

    const allPromises:Promise<any>[] = [];
    allPromises.push(this.productMappingResource.get(this.fiId));
    allPromises.push(this.engagementRewardResource.getAll(this.fiId));

    Promise.all(allPromises).then((results:any[]) => {
      this.buildProductMap(results[0]);

      results[1].forEach((reward:EngagementReward) => {
        this.allRewards.set(reward.id!.toString(), reward);
        const item:RewardTableItem | null = this.buildTableItem(reward);
        if (item) {
          this.tableItems.push(item);
        }
      });

      this.loadedFiId = this.fiId;

      if (editMode) {
        this.editReward();
      }
    }).catch((error) => {
      this.showError(error);
    }).finally(() => {
      this.loading = false;
    });
  }

  buildProductMap(productMappings:ProductMapping[]) {
    productMappings.forEach((productMapping:ProductMapping) => {
      this.productMap.set(productMapping.externalProductMappingId!, productMapping);
    });
  }

  buildTableItem(reward:EngagementReward) : RewardTableItem | null {
    const displayVersion:EngagementRewardVersion | null = reward.getDisplayVersion();

    if (displayVersion) {
      return {
        rewardName: displayVersion.name,
        rewardType: EngagementRewardTypeNames.get(displayVersion.rewardType)!,
        productNames: this.buildProductNameString(displayVersion),
        active: EngagementRewards.displayedRewardIsActive(displayVersion) ? 'Yes' : 'No',
        id: displayVersion.id!,
        designStartDate: displayVersion.designStartDate,
      };
    }

    return null;
  }

  buildProductNameString(rewardVersion:EngagementRewardVersion) : string {
    let productNameString:string = '';
    for (let i = 0; i < rewardVersion.products.length; i++) {
      productNameString += this.productMap.has(rewardVersion.products[i]) ? this.productMap.get(rewardVersion.products[i])!.fiProductName : '?';

      if (i < rewardVersion.products.length - 1) {
        productNameString += ', ';
      }
    }
    return productNameString;
  }

  static displayedRewardIsActive(reward:EngagementRewardVersion) {
    let active: boolean = false;
    const today:number = DateTool.getAustinDate().getTime();
    const designStartDate:number = DateTool.parseDate(reward.designStartDate).getTime();

    if (designStartDate <= today) {
      active = (reward.startDate === null || DateTool.parseDate(reward.startDate).getTime() <= today)
        && (reward.endDate === null || DateTool.parseDate(reward.endDate).getTime() >= today);
    }

    return active;
  }

  editReward() {
    const rewardId: string | undefined = this.$route.params.rewardId;
    const designStartDate: string | undefined = this.$route.params.designStartDate;
    this.editingExisting = false;

    if (rewardId !== undefined && rewardId.length > 0 && designStartDate !== undefined && designStartDate) {
      this.selectedReward = this.findReward(rewardId);

      if (this.selectedReward === null) {
        this.showError(`No reward with id ${rewardId} found.`);
        this.viewAllRewards();
      } else {
        const version: EngagementRewardVersion | null = this.selectedReward.findVersion(designStartDate);
        if (!version) {
          this.showError(`No reward version with design start date ${designStartDate} found.`);
          this.viewAllRewards();
        } else {
          this.workingRewardVersion = Object.assign({}, version);
          this.originalStartDate = version.designStartDate;
          this.isEditable = false;
          this.rewardHeaderItems[2].text = this.workingRewardVersion!.name;
        }
      }
    } else {
      this.workingRewardVersion = null;
      this.selectedReward = new EngagementReward(this.fiId);
      this.workingRewardVersion = Object.assign({}, this.selectedReward.versions[0]);
      this.rewardHeaderItems[2].text = 'New Engagement Reward';
      this.isEditable = true;
    }

    this.title = this.rewardHeaderItems[2].text;
  }

  isRewardsEditorValid() {
    return this.editPanelFormValid;
  }

  onEditClicked() {
    this.isEditable = true;
    this.editingExisting = true;
  }

  onEditFormValidChange(change: boolean) {
    this.editPanelFormValid = change;
  }

  findReward(rewardId: string) : EngagementReward | null {
    let found: EngagementReward | null = null;
    if (this.allRewards.has(rewardId)) {
      found = this.allRewards.get(rewardId)!;
    }
    return found;
  }

  findNextVersionDateOpening(rewardId: number|null) : string {
    const date = DateTool.getAustinDateNoTimeComponent();
    if (!rewardId) {
      return DateTool.toIsoDateString(date);
    }

    const rewards : EngagementReward | undefined = this.allRewards.get(rewardId.toString());
    if (!rewards) {
      return DateTool.toIsoDateString(date);
    }

    while (rewards.findVersion(DateTool.toIsoDateString(date))) {
      date.setDate(date.getDate() + 1);
    }

    return DateTool.toIsoDateString(date);
  }

  createNewVersion() {
    if (this.workingRewardVersion) {
      this.workingRewardVersion = Object.assign({}, this.workingRewardVersion);
      this.workingRewardVersion!.designStartDate = this.findNextVersionDateOpening(this.workingRewardVersion.id);
      this.workingRewardVersion.activatedAtLeastOnce = false;
      this.isEditable = true;
      this.editingExisting = false;

      if (this.workingRewardVersion.durationType !== 'ACTIVE_ALWAYS') {
        this.workingRewardVersion!.startDate = this.workingRewardVersion!.designStartDate;
      }
    }
  }

  onSave() {
    this.saving = true;

    if (this.workingRewardVersion?.durationType === 'ACTIVE_ALWAYS') {
      this.workingRewardVersion!.startDate = null;
      this.workingRewardVersion!.endDate = null;
    }

    this.engagementRewardResource.save(this.fiId, this.workingRewardVersion!)
      .then((savedVersion:EngagementRewardVersion) => {
        this.loadRewards(false);
        this.viewAllRewards();
        this.showSuccess('Successfully saved engagement reward.');
      }).catch((error:any) => {
        this.showError(error);
      }).finally(() => {
        this.saving = false;
      });
  }

  onDelete() {
    const dialog = new Dialog('Confirm Deletion',
      'Are you sure you want to delete this engagement reward version?');
    dialog.setAcceptLabel('OK')
      .setDeclineLabel('Cancel');
    this.$store.dispatch('notices/add', dialog)
      .then((ok) => {
        if (ok === 'accept') {
          this.saving = true;
          this.engagementRewardResource.delete(this.fiId, this.selectedReward!.id!, this.workingRewardVersion?.designStartDate!).then((result: EngagementRewardDeleteResponse) => {
            if (!result.success) {
              this.setError(`Engagement Reward not deleted.  Reason: ${result.reason}`);
            } else {
              this.loadRewards(false);
              this.viewAllRewards();
            }
          }).catch((error) => {
            this.showError(error);
          }).then(() => {
            this.saving = false;
          });
        }
      });
  }

  isDeletable() {
    if (!this.workingRewardVersion) {
      return false;
    }

    return !this.workingRewardVersion.activatedAtLeastOnce;
  }

  isDeleteButtonDisabled() {
    return !this.isEditable || this.saving || !this.editingExisting || !this.isDeletable();
  }

  showError(error: any) {
    if (error.response && error.response.data && error.response.data.entity) {
      this.setError(error.response.data.entity);
    } else if (error.response.data && error.response.data.data && error.response.data.data[0] && error.response.data.data[0].message) {
      this.setError(error.response.data.data[0].message);
    } else {
      this.setError('An unexpected error occurred.');
    }
  }

  setError(message: string) {
    const al = new Alert(message, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }

  showSuccess(message: string) {
    const al = new Alert(message, NoticeClass.SUCCESS);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
